import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  RequestItem,
  useAdministrationRequestApproveAPI,
  useCurrentRequestById,
  useDefaultRequestById,
  useRequestsAPI,
  useResizeObserver,
  useVacanciesAPI,
  useVacanciesOptionsApi,
  useValidationSchemaRequest,
} from '@innowise-group/core';
import { requestFormDefaultValues } from '@innowise-group/modals';
import { Loader, Modal, modalHoc, ModalProps, useModal, useTheme } from '@innowise-group/mui-kit';
import { RequestForm } from '@shared-components';

import { RequestNonBillableWarningModal } from '../request-non-billable-warning-modal';
import * as Styled from './request-edition-modal.styles';

interface RequestEditionModalProps {
  requestId: number;
  successEditionCallback?: () => void;
}

const RequestEditionModal: React.FC<ModalProps & RequestEditionModalProps> = ({
  close,
  requestId,
  successEditionCallback,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktopView = useResizeObserver(theme.breakpoints.values.sm);

  const requestDefault = useDefaultRequestById();
  const { getVacanciesByIds } = useVacanciesOptionsApi();
  const initialRequestEntity = useCurrentRequestById();

  const { isLoading: requestsIsLoading, updateRequest } = useRequestsAPI();
  const { fetchAllVacancies, isLoading: vacanciesIsLoading } = useVacanciesAPI();
  const { getAdministrationRequestApprove, isLoading: requestApproversIsLoading } =
    useAdministrationRequestApproveAPI();

  const { validationSchema } = useValidationSchemaRequest();
  const methods = useForm<RequestItem>({
    mode: 'all',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: requestFormDefaultValues,
  });

  const { handleSubmit, reset, watch } = methods;
  const isBillable = watch('isBillable');

  const [showRequestNonBillableWarningModal] = useModal(RequestNonBillableWarningModal);

  const isNonBillableWarningToBeShown = useMemo(() => {
    if (isBillable === 'false' && initialRequestEntity.isBillable) {
      return true;
    }

    return false;
  }, [isBillable, initialRequestEntity?.isBillable]);

  const onSubmit = (data: RequestItem) => {
    updateRequest(data, requestId, successCallback);

    if (isNonBillableWarningToBeShown) {
      showRequestNonBillableWarningModal();
    }
  };

  const successCallback = () => {
    close();
    successEditionCallback?.();
  };

  useEffect(() => {
    fetchAllVacancies();
    getAdministrationRequestApprove();
  }, [fetchAllVacancies, getAdministrationRequestApprove]);

  useEffect(() => {
    if (requestDefault?.vacancyId) {
      getVacanciesByIds([requestDefault.vacancyId]);
    }
    reset(requestDefault);
  }, [requestDefault?.vacancyId]);

  const selectedAuthorsOptions = useMemo(() => {
    if (!requestDefault) return [];
    return requestDefault.authorId.map((item) => item.value);
  }, [requestDefault?.vacancyId]);

  return (
    <Modal.Container size="large">
      {requestsIsLoading || vacanciesIsLoading || requestApproversIsLoading ? (
        <Loader />
      ) : (
        <FormProvider {...methods}>
          <Modal.Header onClose={close}>
            <Styled.Header>
              <span>{t('pages.requests.creationModal.requestEdition')}</span>
            </Styled.Header>
          </Modal.Header>
          <Styled.Body>
            <RequestForm
              isEdit
              onSubmit={onSubmit}
              handleClose={close}
              selectedAuthorsOptions={selectedAuthorsOptions}
              isNonBillableWarningToBeShown={isNonBillableWarningToBeShown}
            />
          </Styled.Body>
          {isDesktopView && (
            <Styled.Footer>
              <Styled.CancelButton onClick={close} variant="outlined">
                {t('buttons.cancel')}
              </Styled.CancelButton>
              <Styled.SubmitButton onClick={handleSubmit(onSubmit)}>{t('buttons.save')}</Styled.SubmitButton>
            </Styled.Footer>
          )}
        </FormProvider>
      )}
    </Modal.Container>
  );
};

export default modalHoc(RequestEditionModal, 'request-edition');
